// ========================================
// AUTO PERSONALE SECTION RESPONSABILI - VERSIONE AGGIORNATA CON CONTROLLO TURNO ATTIVO
// Per Responsabili - Modal manuale solo se: NO ore E NO turno attivo
// NOTA: Versione identica a AutoPersonaleSection ma senza riferimenti a costi/ricavi nell'interfaccia
// src/components/Admin/Cantieri/AutoPersonaleSectionResponsabili.jsx
// ========================================

import React, { useState, useEffect, useMemo } from 'react';
import { PersonaleAutocomplete } from './AutocompleteComponents';
import { getWorkHoursForDate } from '../../../services/workHoursIntegrationService';
import { syncWorkHoursForReport } from '../../../services/workHoursService';
import { syncRapportiniAfterWorkHoursChange } from '../../../services/clientSideRapportiniSync';
import { checkActiveShift } from '../../../services/activeShiftChecker'; // 🆕 AGGIUNTO
import './AutoPersonaleSection.css';
import './ManualHoursModal.css';

const AutoPersonaleSectionResponsabili = ({
  users = [],
  formData,
  setFormData,
  rapportinoDate,
  editingRapportino = null,
  onRapportinoUpdate
}) => {
  const [selectedPersona, setSelectedPersona] = useState('');
  const [previewOre, setPreviewOre] = useState(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);

  // 🆕 NUOVO: Ore uguali per tutti i presenti
  const [oreUgualiPerTutti, setOreUgualiPerTutti] = useState('');
  
  // 🆕 NUOVO: Stati per modal inserimento manuale ore
  const [showManualHoursModal, setShowManualHoursModal] = useState(false);
  const [manualHoursData, setManualHoursData] = useState({
    oreStandard: '',
    oreStraordinario: '',
    note: ''
  });
  const [pendingPersonaData, setPendingPersonaData] = useState(null);

  // Calcola totale ore lavorate nel rapportino
  const totalHoursInRapportino = useMemo(() => {
    if (!formData.personalePresente || formData.personalePresente.length === 0) {
      return 0;
    }
    
    return formData.personalePresente.reduce((sum, persona) => {
      if (persona.statoPresenza === 'presente') {
        return sum + (parseFloat(persona.oreLavorate) || 0);
      }
      return sum;
    }, 0);
  }, [formData.personalePresente]);

  // Carica anteprima ore quando viene selezionata una persona
  useEffect(() => {
    const loadPreview = async () => {
      if (!selectedPersona || !rapportinoDate) {
        setPreviewOre(null);
        return;
      }

      setIsLoadingPreview(true);
      try {
        const workHours = await getWorkHoursForDate(
          selectedPersona, 
          rapportinoDate, 
          false, // respectDistribution = false per anteprima
          null   // currentRapportinoId = null
        );
        
        if (workHours && workHours.found) {
          setPreviewOre({
            ordinarie: workHours.oreStandard || 0,
            straordinarie: workHours.oreStraordinario || 0,
            totale: (workHours.oreStandard || 0) + (workHours.oreStraordinario || 0),
            found: true,
            isSpecialDay: workHours.isSpecialDay || false
          });
        } else {
          setPreviewOre({ 
            ordinarie: 0, 
            straordinarie: 0, 
            totale: 0,
            found: false 
          });
        }
      } catch (error) {
        console.error('Errore nel caricamento anteprima:', error);
        setPreviewOre(null);
      } finally {
        setIsLoadingPreview(false);
      }
    };

    loadPreview();
  }, [selectedPersona, rapportinoDate]);

  // 🆕 NUOVO: Mostra modal per inserimento manuale ore
  const showManualHoursDialog = (selectedUser) => {
    setPendingPersonaData(selectedUser);
    setManualHoursData({
      oreStandard: '',
      oreStraordinario: '',
      note: 'Ore inserite manualmente da rapportino'
    });
    setShowManualHoursModal(true);
  };

  // 🆕 NUOVO: Chiudi modal e reset
  const closeManualHoursModal = () => {
    setShowManualHoursModal(false);
    setManualHoursData({
      oreStandard: '',
      oreStraordinario: '',
      note: ''
    });
    setPendingPersonaData(null);
  };

  // 🆕 MODIFICATO: Conferma inserimento manuale e aggiungi persona (SOLO IN MEMORIA)
  const handleConfirmManualHours = async () => {
    // Validazione ore
    const oreStandard = parseFloat(manualHoursData.oreStandard) || 0;
    const oreStraordinario = parseFloat(manualHoursData.oreStraordinario) || 0;

    if (oreStandard < 0 || oreStandard > 24) {
      alert('❌ Le ore standard devono essere tra 0 e 24');
      return;
    }

    if (oreStraordinario < 0 || oreStraordinario > 24) {
      alert('❌ Le ore straordinario devono essere tra 0 e 24');
      return;
    }

    if (oreStandard === 0 && oreStraordinario === 0) {
      alert('❌ Inserisci almeno un\'ora (standard o straordinario)');
      return;
    }

    // 🆕 NON SALVARE NEL WORKHOURS SUBITO!
    // Aggiungi solo la persona al rapportino con un flag "pendingManualHours"
    // Le ore verranno salvate nel workHours solo quando si salva il rapportino
    
    const oreTotali = oreStandard + oreStraordinario;
    const costoOrario = parseFloat(pendingPersonaData.costoOrario || 0);

    const nuovaPersona = {
      personaId: pendingPersonaData.id,
      nome: pendingPersonaData.nome || '',
      cognome: pendingPersonaData.cognome || '',
      statoPresenza: 'presente',
      oreLavorate: oreTotali,
      oreOrdinarie: oreStandard,
      oreStraordinarie: oreStraordinario,
      costoOrario: costoOrario,
      totale: oreTotali * costoOrario,
      notePersona: manualHoursData.note || 'Ore inserite manualmente',
      source: 'manual-input',
      // 🆕 FLAG IMPORTANTE: indica che queste ore devono essere salvate nel workHours
      pendingManualHours: {
        oreStandard: oreStandard,
        oreStraordinario: oreStraordinario,
        note: manualHoursData.note || 'Ore inserite manualmente da rapportino',
        date: rapportinoDate
      }
    };

    const personalePresente = formData.personalePresente || [];
    setFormData({
      ...formData,
      personalePresente: [...personalePresente, nuovaPersona]
    });

    // Chiudi modal
    closeManualHoursModal();

    // Reset selezione
    setSelectedPersona('');
    setPreviewOre(null);

    // Feedback successo
    const nomeCompleto = `${pendingPersonaData.nome} ${pendingPersonaData.cognome}`;
    alert(`✅ ${nomeCompleto} aggiunto/a con successo!\n\n⚠️ IMPORTANTE: Le ore verranno salvate nel monte ore quando salvi il rapportino.`);
  };

  // 🆕 NUOVA FUNZIONE: Aggiungi con 0 ore direttamente dal modal
  const handleConfirmZeroHours = () => {
    if (!pendingPersonaData) return;

    const costoOrario = parseFloat(pendingPersonaData.costoOrario || 0);
    const nuovaPersona = {
      personaId: pendingPersonaData.id,
      nome: pendingPersonaData.nome || '',
      cognome: pendingPersonaData.cognome || '',
      statoPresenza: 'presente',
      oreLavorate: 0,
      oreOrdinarie: 0,
      oreStraordinarie: 0,
      costoOrario: costoOrario,
      totale: 0,
      notePersona: 'Nessuna timbratura - ore non trovate',
      source: 'no-hours'
    };

    const personalePresente = formData.personalePresente || [];
    setFormData({
      ...formData,
      personalePresente: [...personalePresente, nuovaPersona]
    });

    closeManualHoursModal();
    alert(`✅ ${pendingPersonaData.nome} ${pendingPersonaData.cognome} aggiunto/a con 0 ore`);
  };

  // 🆕 MODIFICATO: Handler per aggiungere personale CON CONTROLLO TURNO ATTIVO
  const handleAddPersonale = async () => {
    if (!selectedPersona) {
      alert('Seleziona una persona dal menu');
      return;
    }

    // Verifica se la persona è già presente
    const personalePresente = formData.personalePresente || [];
    const giaPresente = personalePresente.some(p => p.personaId === selectedPersona);
    
    if (giaPresente) {
      alert('Questa persona è già stata aggiunta al rapportino');
      return;
    }

    const selectedUser = users.find(u => u.id === selectedPersona);
    if (!selectedUser) {
      alert('Persona non trovata');
      return;
    }

    // 🔥 NUOVA LOGICA: Controlla sia le ore CHE il turno attivo
    const hasHours = previewOre && previewOre.found && 
                     (previewOre.ordinarie > 0 || previewOre.straordinarie > 0 || previewOre.isSpecialDay);
    
    if (!hasHours) {
      // Nessuna ora trovata - Controlla se c'è un turno attivo
      console.log(`⚠️ Nessuna ora trovata per ${selectedUser.nome} ${selectedUser.cognome} - Controllo turno attivo...`);
      
      try {
        const activeShiftStatus = await checkActiveShift(selectedPersona, rapportinoDate);
        
        if (activeShiftStatus.hasActiveShift) {
          // 🟢 C'È UN TURNO ATTIVO - Aggiungi con 0 ore senza modal
          const nomeCompleto = `${selectedUser.nome} ${selectedUser.cognome}`;
          
          const userChoice = window.confirm(
            `⏳ TURNO IN CORSO PER ${nomeCompleto}\n\n` +
            `Timbratura di ingresso: ${activeShiftStatus.shiftData.clockInTime}\n` +
            `Data: ${rapportinoDate}\n\n` +
            `✅ Le ore verranno calcolate automaticamente dopo la timbratura di uscita.\n\n` +
            `Vuoi aggiungere questa persona al rapportino con 0 ore?\n` +
            `(Le ore si aggiorneranno automaticamente)`
          );

          if (userChoice) {
            // Aggiungi con 0 ore - il sistema sincronizzerà dopo la timbratura di uscita
            const costoOrario = parseFloat(selectedUser.costoOrario || 0);
            const nuovaPersona = {
              personaId: selectedUser.id,
              nome: selectedUser.nome || '',
              cognome: selectedUser.cognome || '',
              statoPresenza: 'presente',
              oreLavorate: 0,
              oreOrdinarie: 0,
              oreStraordinarie: 0,
              costoOrario: costoOrario,
              totale: 0,
              notePersona: `Turno attivo - ingresso: ${activeShiftStatus.shiftData.clockInTime}`,
              source: 'active-shift',
              activeShiftInfo: {
                clockInTime: activeShiftStatus.shiftData.clockInTime,
                date: rapportinoDate,
                message: 'Ore in attesa di timbratura di uscita'
              }
            };

            setFormData({
              ...formData,
              personalePresente: [...personalePresente, nuovaPersona]
            });

            alert(
              `✅ ${nomeCompleto} aggiunto/a con turno attivo!\n\n` +
              `⏳ Ingresso: ${activeShiftStatus.shiftData.clockInTime}\n` +
              `🔄 Le ore si aggiorneranno automaticamente dopo l'uscita`
            );
          }
          
          // Reset selezione
          setSelectedPersona('');
          setPreviewOre(null);
          return;
        }
        
        // 🔴 NESSUNA ORA E NESSUN TURNO ATTIVO - MOSTRA DIRETTAMENTE IL MODAL INSERIMENTO MANUALE
        console.log(`❌ Nessuna ora e nessun turno attivo per ${selectedUser.nome} ${selectedUser.cognome}`);
        
        showManualHoursDialog(selectedUser);
        
        // Reset selezione
        setSelectedPersona('');
        setPreviewOre(null);
        return;
        
      } catch (error) {
        console.error('❌ Errore controllo turno attivo:', error);
        alert(`⚠️ Errore nel controllo del turno attivo.\n\n${error.message}\n\nLa persona verrà aggiunta con 0 ore.`);
        
        // Fallback: aggiungi con 0 ore in caso di errore
        const costoOrario = parseFloat(selectedUser.costoOrario || 0);
        const nuovaPersona = {
          personaId: selectedUser.id,
          nome: selectedUser.nome || '',
          cognome: selectedUser.cognome || '',
          statoPresenza: 'presente',
          oreLavorate: 0,
          oreOrdinarie: 0,
          oreStraordinarie: 0,
          costoOrario: costoOrario,
          totale: 0,
          notePersona: 'Errore verifica turno - ore non trovate',
          source: 'error-fallback'
        };

        setFormData({
          ...formData,
          personalePresente: [...personalePresente, nuovaPersona]
        });
        
        setSelectedPersona('');
        setPreviewOre(null);
        return;
      }
    }

    // ✅ ORE TROVATE - Aggiungi normalmente
    const oreLavorate = previewOre.totale || 0;
    const oreOrdinarie = previewOre.ordinarie || 0;
    const oreStraordinarie = previewOre.straordinarie || 0;
    
    // 🔧 FIX: Recupera il costo orario dal database utente
const costoOrario = parseFloat(selectedUser.costoOrario || 0);

const nuovaPersona = {
  personaId: selectedUser.id,
  nome: selectedUser.nome || '',
  cognome: selectedUser.cognome || '',
  statoPresenza: 'presente',
  oreLavorate: oreLavorate,
  oreOrdinarie: oreOrdinarie,
  oreStraordinarie: oreStraordinarie,
  // 🔧 FIX: AGGIUNGI costo orario e totale
  costoOrario: costoOrario,
  totale: oreLavorate * costoOrario,
  notePersona: previewOre.isSpecialDay ? 'Giorno speciale' : '',
  source: 'workhours'
};

    setFormData({
      ...formData,
      personalePresente: [...personalePresente, nuovaPersona]
    });

    const nomeCompleto = `${selectedUser.nome} ${selectedUser.cognome}`;
    alert(`✅ ${nomeCompleto} aggiunto/a con successo!\n\n⏰ Ore: ${oreLavorate}h (${oreOrdinarie}h std + ${oreStraordinarie}h straord)`);
    
    // Reset selezione
    setSelectedPersona('');
    setPreviewOre(null);
  };

  // Handler per rimuovere personale
  const handleRemovePersonale = (index) => {
    const persona = formData.personalePresente[index];
    const nomeCompleto = `${persona.nome} ${persona.cognome}`;
    
    if (window.confirm(`Vuoi rimuovere ${nomeCompleto} dal rapportino?`)) {
      const newPersonalePresente = formData.personalePresente.filter((_, i) => i !== index);
      setFormData({
        ...formData,
        personalePresente: newPersonalePresente
      });
    }
  };

// Handler per aggiornare le ore di una persona
const handleUpdateOre = (index, field, value) => {
  const personalePresente = [...formData.personalePresente];
  const persona = personalePresente[index];
  
  const numValue = parseFloat(value) || 0;
  
  if (field === 'oreOrdinarie') {
    persona.oreOrdinarie = numValue;
  } else if (field === 'oreStraordinarie') {
    persona.oreStraordinarie = numValue;
  }
  
  // Ricalcola totale ore
  persona.oreLavorate = (persona.oreOrdinarie || 0) + (persona.oreStraordinarie || 0);
  
  // 🔧 FIX CRITICO: RICALCOLA IL TOTALE COSTO
  const costoOrario = parseFloat(persona.costoOrario || 0);
  persona.totale = persona.oreLavorate * costoOrario;
  
  console.log(`💰 [RESPONSABILE-AUTO] Ricalcolo: ${persona.nome} - ${persona.oreLavorate}h × €${costoOrario} = €${persona.totale.toFixed(2)}`);
  
  personalePresente[index] = persona;

  setFormData({
    ...formData,
    personalePresente: personalePresente
  });
};

  // 🆕 NUOVO: Applica la stessa ora (standard) a tutti i presenti nel rapportino
  // Le ore impostate qui vengono marcate come "pendingManualHours" così, al salvataggio
  // del rapportino, vengono scritte nel monte ore (workHours) di ciascuna persona
  // per la data selezionata, esattamente come per l'inserimento manuale singolo.
  const handleApplyOreUgualiPerTutti = () => {
    const valore = parseFloat(oreUgualiPerTutti);

    // Il monte ore (workHours) accetta solo ore standard intere tra 0 e 8
    if (isNaN(valore) || !Number.isInteger(valore) || valore < 0 || valore > 8) {
      alert('❌ Inserisci un numero intero di ore tra 0 e 8 (limite del monte ore)');
      return;
    }

    const personalePresente = formData.personalePresente || [];
    if (personalePresente.length === 0) {
      alert('⚠️ Nessun personale presente nel rapportino a cui applicare le ore');
      return;
    }

    const nuovoPersonalePresente = personalePresente.map(persona => {
      if (persona.statoPresenza === 'assente') {
        return persona;
      }

      const costoOrario = parseFloat(persona.costoOrario || 0);

      return {
        ...persona,
        oreOrdinarie: valore,
        oreStraordinarie: 0,
        oreLavorate: valore,
        totale: valore * costoOrario,
        pendingManualHours: {
          oreStandard: valore,
          oreStraordinario: 0,
          note: 'Ore impostate in blocco da rapportino',
          date: rapportinoDate
        }
      };
    });

    setFormData({
      ...formData,
      personalePresente: nuovoPersonalePresente
    });

    alert(`✅ Ore impostate a ${valore}h per tutti i presenti.\n\n⚠️ Verranno salvate nel monte ore al salvataggio del rapportino.`);
  };

  // Componente badge per indicare la fonte dei dati
  const SourceBadge = ({ source, activeShiftInfo }) => {
    if (source === 'manual-input') {
      return (
        <span className="badge badge-manual ml-2" title="Ore inserite manualmente">
          ✏️ Manuale
        </span>
      );
    }
    if (source === 'active-shift') {
      return (
        <span className="badge badge-info ml-2" title={`Turno attivo - ingresso: ${activeShiftInfo?.clockInTime}`}>
          ⏳ Turno in corso
        </span>
      );
    }
    if (source === 'no-hours') {
      return (
        <span className="badge badge-warning ml-2" title="Nessuna timbratura trovata">
          ⚠️ Senza ore
        </span>
      );
    }
    return null;
  };

  return (
    <div className="auto-personale-section">
      <h3>👥 Gestione Personale Presente</h3>
      
      {/* Form aggiunta personale */}
      <div className="add-personale-form">
        <div className="form-row">
          <div className="form-group flex-grow">
            <label>Seleziona Persona</label>
            <PersonaleAutocomplete
              users={users}
              selectedPersona={selectedPersona}
              setSelectedPersona={setSelectedPersona}
              placeholder="Cerca persona..."
            />
          </div>
          
          <div className="form-group">
            <label>&nbsp;</label>
            <button
              type="button"
              onClick={handleAddPersonale}
              className="btn btn-primary"
              disabled={!selectedPersona}
            >
              ➕ Aggiungi
            </button>
          </div>
        </div>

        {/* Anteprima ore per persona selezionata */}
        {selectedPersona && (
          <div className="hours-preview">
            {isLoadingPreview ? (
              <p className="loading-text">⏳ Caricamento ore...</p>
            ) : previewOre ? (
              <div className={`preview-card ${previewOre.found ? 'found' : 'not-found'}`}>
                <h4>📊 Anteprima Ore</h4>
                {previewOre.found ? (
                  <>
                    {previewOre.isSpecialDay ? (
                      <p className="special-day">📅 Giorno speciale</p>
                    ) : (
                      <div className="hours-breakdown">
                        <div className="hour-item">
                          <span className="hour-label">Ordinarie:</span>
                          <span className="hour-value">{previewOre.ordinarie}h</span>
                        </div>
                        <div className="hour-item">
                          <span className="hour-label">Straordinarie:</span>
                          <span className="hour-value">{previewOre.straordinarie}h</span>
                        </div>
                        <div className="hour-item total">
                          <span className="hour-label">Totale:</span>
                          <span className="hour-value">{previewOre.totale}h</span>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="no-hours-warning">
                    <p>⚠️ Nessuna ora trovata</p>
                    <p className="hint">Verrà verificato anche il turno attivo...</p>
                  </div>
                )}
              </div>
            ) : null}
          </div>
        )}
      </div>

      {/* Modal inserimento manuale ore */}
      {showManualHoursModal && pendingPersonaData && (
        <div className="modal-overlay" onClick={closeManualHoursModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>⏰ Inserimento Manuale Ore</h3>
              <button className="close-btn" onClick={closeManualHoursModal}>✕</button>
            </div>
            
            <div className="modal-body">
              <p className="modal-info">
                Inserisci le ore per <strong>{pendingPersonaData?.nome} {pendingPersonaData?.cognome}</strong> 
                in data <strong>{rapportinoDate}</strong>
              </p>
              <p className="modal-warning">
                ⚠️ Le ore verranno salvate automaticamente nel monte ore della persona.
              </p>

              <div className="form-group">
                <label>Ore Standard</label>
                <input
                  type="number"
                  min="0"
                  max="24"
                  step="0.5"
                  value={manualHoursData.oreStandard}
                  onChange={(e) => setManualHoursData(prev => ({
                    ...prev,
                    oreStandard: e.target.value
                  }))}
                  placeholder="Es: 8"
                  autoFocus
                />
              </div>

              <div className="form-group">
                <label>Ore Straordinario</label>
                <input
                  type="number"
                  min="0"
                  max="24"
                  step="0.5"
                  value={manualHoursData.oreStraordinario}
                  onChange={(e) => setManualHoursData(prev => ({
                    ...prev,
                    oreStraordinario: e.target.value
                  }))}
                  placeholder="Es: 2"
                />
              </div>
            </div>

            <div className="modal-footer">
              <button 
                className="btn btn-secondary" 
                onClick={closeManualHoursModal}
              >
                Annulla
              </button>
              <button 
                className="btn btn-warning" 
                onClick={handleConfirmZeroHours}
                style={{ marginRight: 'auto' }}
              >
                ➕ Aggiungi con 0 ore
              </button>
              <button 
                className="btn btn-primary" 
                onClick={handleConfirmManualHours}
                disabled={
                  !manualHoursData.oreStandard && 
                  !manualHoursData.oreStraordinario
                }
              >
                💾 Salva e Aggiungi
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 🆕 NUOVO: Imposta ore uguali per tutti i presenti */}
      {formData.personalePresente && formData.personalePresente.length > 0 && (
        <div className="add-personale-form ore-uguali-form">
          <div className="form-row">
            <div className="form-group">
              <label>Ore uguali per tutti</label>
              <input
                type="number"
                min="0"
                max="8"
                step="1"
                value={oreUgualiPerTutti}
                onChange={(e) => setOreUgualiPerTutti(e.target.value)}
                placeholder="Es: 8"
                className="form-control form-control-sm"
              />
            </div>
            <div className="form-group">
              <label>&nbsp;</label>
              <button
                type="button"
                onClick={handleApplyOreUgualiPerTutti}
                className="btn btn-secondary"
                disabled={oreUgualiPerTutti === ''}
              >
                ⏱️ Applica a tutti i presenti
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Lista personale presente */}
      {formData.personalePresente && formData.personalePresente.length > 0 ? (
        <div className="personale-list">
          <table className="table">
            <thead>
              <tr>
                <th>Nome</th>
                <th>Ore Ordinarie</th>
                <th>Ore Straord.</th>
                <th>Totale Ore</th>
                <th>Azioni</th>
              </tr>
            </thead>
            <tbody>
              {formData.personalePresente.map((persona, index) => {
                const user = users.find(u => u.id === persona.personaId);
                const nomeCompleto = user 
                  ? `${user.nome} ${user.cognome}`
                  : `${persona.nome} ${persona.cognome}`;

                return (
                  <tr key={index}>
                    <td>
                      <strong>{nomeCompleto}</strong>
                      <SourceBadge 
                        source={persona.source} 
                        activeShiftInfo={persona.activeShiftInfo}
                      />
                      {persona.statoPresenza === 'assente' && (
                        <span className="badge badge-warning ml-2">Assente</span>
                      )}
                    </td>
                    <td>
                      <input
                        type="number"
                        step="0.5"
                        min="0"
                        max="24"
                        value={persona.oreOrdinarie || 0}
                        onChange={(e) => handleUpdateOre(index, 'oreOrdinarie', e.target.value)}
                        className="form-control form-control-sm"
                        disabled={persona.statoPresenza === 'assente'}
                      />
                    </td>
                    <td>
                      <input
                        type="number"
                        step="0.5"
                        min="0"
                        max="24"
                        value={persona.oreStraordinarie || 0}
                        onChange={(e) => handleUpdateOre(index, 'oreStraordinarie', e.target.value)}
                        className="form-control form-control-sm"
                        disabled={persona.statoPresenza === 'assente'}
                      />
                    </td>
                    <td>
                      <strong className="text-primary">
                        {persona.oreLavorate?.toFixed(2) || '0.00'}h
                      </strong>
                    </td>
                    <td>
                      <button
                        type="button"
                        onClick={() => handleRemovePersonale(index)}
                        className="btn btn-danger btn-sm"
                      >
                        🗑️
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          
          {/* Riepilogo totale ore */}
          <div className="hours-summary">
            <strong>Totale ore lavorate nel rapportino:</strong> {totalHoursInRapportino.toFixed(2)}h
          </div>
        </div>
      ) : (
        <div className="empty-state">
          <p>Nessun personale aggiunto. Usa il form sopra per aggiungere persone al rapportino.</p>
        </div>
      )}
    </div>
  );
};

/**
 * 🆕 FUNZIONE HELPER ESPORTATA
 * Sincronizza le ore manuali pending con il workHours
 * Deve essere chiamata dal componente padre DOPO aver salvato il rapportino
 */
export const syncPendingManualHours = async (personalePresente, rapportinoDate) => {
  if (!personalePresente || personalePresente.length === 0) {
    return { success: true, syncedCount: 0 };
  }

  const results = [];
  let syncedCount = 0;
  let errorCount = 0;

  for (const persona of personalePresente) {
    // Controlla se questa persona ha ore manuali da sincronizzare
    if (persona.pendingManualHours) {
      try {
        console.log(`💾 Sincronizzazione ore manuali per ${persona.nome} ${persona.cognome}`, {
          userId: persona.personaId,
          date: persona.pendingManualHours.date,
          oreStandard: persona.pendingManualHours.oreStandard,
          oreStraordinario: persona.pendingManualHours.oreStraordinario
        });

        await syncWorkHoursForReport({
          userId: persona.personaId,
          date: persona.pendingManualHours.date,
          standardHours: persona.pendingManualHours.oreStandard,
          overtimeHours: persona.pendingManualHours.oreStraordinario,
          notes: persona.pendingManualHours.note || 'Ore inserite manualmente da rapportino'
        });

        syncedCount++;
        results.push({
          success: true,
          personaId: persona.personaId,
          nome: `${persona.nome} ${persona.cognome}`
        });

        console.log(`✅ Ore sincronizzate per ${persona.nome} ${persona.cognome}`);
      } catch (error) {
        errorCount++;
        results.push({
          success: false,
          personaId: persona.personaId,
          nome: `${persona.nome} ${persona.cognome}`,
          error: error.message
        });
        console.error(`❌ Errore sincronizzazione ore per ${persona.nome} ${persona.cognome}:`, error);
      }
    }
  }

  console.log(`📊 Sincronizzazione completata: ${syncedCount} successi, ${errorCount} errori`);

  return {
    success: errorCount === 0,
    syncedCount,
    errorCount,
    results
  };
};

export default AutoPersonaleSectionResponsabili;